import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { queueApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { EXPLORE_QUEUE_WS_EVENTS } from "./constants";

export function useExploreQueueWs({
  enabled,
  jobId,
  removeMatchLocally,
}: {
  enabled: boolean;
  jobId: string | null;
  removeMatchLocally: (matchId: string) => void;
}) {
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !jobId) return;
    let disposed = false;
    let attempts = 0;
    const connect = async () => {
      try {
        const { ticket } = await queueApi.wsTicket(jobId);
        if (disposed) return;
        const ws = new WebSocket(queueApi.wsUrl(jobId, ticket));
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "ping" || msg.type === "subscribed") return;
            if (msg.match_id) removeMatchLocally(msg.match_id);
            if ((EXPLORE_QUEUE_WS_EVENTS as readonly string[]).includes(String(msg.type))) {
              void queryClient.invalidateQueries({ queryKey: ["explore-queue", jobId] });
              void queryClient.invalidateQueries({ queryKey: ["explore-cooldown", jobId] });
              void queryClient.invalidateQueries({ queryKey: ["explore-owned"] });
              void queryClient.invalidateQueries({ queryKey: ["explore-saved", jobId] });
            }
          } catch {
            /* ignore */
          }
        };
        ws.onopen = () => {
          attempts = 0;
        };
        ws.onclose = () => {
          if (disposed) return;
          const delay = Math.min(30_000, 750 * 2 ** attempts) + Math.round(Math.random() * 500);
          attempts += 1;
          reconnectTimerRef.current = window.setTimeout(() => void connect(), delay);
          void queryClient.invalidateQueries({ queryKey: ["explore-queue", jobId] });
        };
      } catch (err) {
        if (disposed) return;
        if (err instanceof ApiError && err.status === 503) return;
        reconnectTimerRef.current = window.setTimeout(() => void connect(), 2_000);
      }
    };
    void connect();
    return () => {
      disposed = true;
      if (reconnectTimerRef.current != null) window.clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [enabled, jobId, queryClient, removeMatchLocally]);
}
